/*
 * Copyright 2023 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import {Clusivity} from '@amzn/id4-mothership/com/amazon/id4/mothership/model/common';
import {SimConfiguration} from '@amzn/id4-mothership/com/amazon/id4/mothership/model/configuration';
import {
    AssignmentMap,
    DedupeStrategy,
    EscalationMap,
    NotificationGrouping,
    ProblemFilter,
    ProblemFilterType,
    StatusChangeStrategy} from '@amzn/id4-mothership/com/amazon/id4/mothership/model/configuration/types';
import {
    SimAccessLevel,
    SimAutoUpgradePolicy,
    SimCategorizationEntry,
    SimIdentity,
    SimTag
} from '@amzn/id4-mothership/com/amazon/id4notificationservice/model/types/sim';
import Alert from '@amzn/meridian/alert';
import {AlertProps} from '@amzn/meridian/alert/alert';
import Box from '@amzn/meridian/box';
import Breadcrumb, {BreadcrumbGroup} from '@amzn/meridian/breadcrumb';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Select, {SelectOption} from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import minusCircleTokens from '@amzn/meridian-tokens/base/icon/minus-circle';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import cron from 'cron-validate';
import React, {useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';

import {AppContext} from '../../../app';
import useWebhookTemplate from '../../../hooks/templates/use-webhook-template';
import useWorkOrderTemplate from '../../../hooks/templates/use-work-order-template';
import {useSearchSelect} from '../../../hooks/use-search-select';
import {selectSelectedRegion} from '../../../state/app/appSlice';
import {getCronError} from '../../../utility/cron-utility';
import {
    getReportConfiguration,
    listProblemFinderRegistrations,
    putReportConfiguration
} from '../../../utility/id4-mothership-client';
import {validateSimConfiguration} from '../../../utility/validate-report-configuration-template';
import SimTemplate from '../../ConfigurationTemplates/SimTemplate';
import {CreationProps} from '../../Inputs/CreationProps';
import SchedulingInput from '../../Inputs/SchedulingInput';
import {RouteName} from '../../Routing/RoutingPage';
import ControlledExpander from '../../utility-views/ControlledExpander';
import LoadingButton from '../../utility-views/LoadingButton';
import {ProblemFilterInput} from './ProblemFilterInput';

const CreateReportConfiguration: React.FC<CreationProps> = ({inEditMode}) => {

    const {webStageConfig} = React.useContext(AppContext);

    const selectedRegion = useSelector(selectSelectedRegion);

    const navigate = useNavigate();

    let {reportConfigurationId} = useParams();

    const previousReportConfigIdRef = useRef<string | undefined>(undefined);

    const [submitted, setSubmitted] = useState<boolean>(false);

    const [alertMessage, setAlertMessage] = useState<{
        type: AlertProps['type'],
        title?: AlertProps['title'],
        message: string,
        link?: { text: string, href: string }
    }>(undefined);

    const [currentProblemFinderIds, setCurrentProblemFinderIds] = React.useState<string[]>([]);
    const [loadingCurrentProblemFinderIds, setLoadingCurrentProblemFinderIds] = React.useState<boolean>(true);

    // Defaulting to uuid for all new report configurations
    const [reportConfigurationIdToUse, setReportConfigurationIdToUse] = React.useState<string>(inEditMode ? reportConfigurationId : undefined);
    const [executionState, setReportConfigExecutionState] = React.useState<string>(undefined);

    const [permissionsTeam, setPermissionsTeam] = React.useState<string>(undefined);
    const [processingMode, setProcessingMode] = React.useState<string>(undefined);

    const [problemFinderIds, setProblemFinderIds, ProblemFinderIdsSelect] = useSearchSelect({
        label: 'Problem finder identifiers',
        placeholder: 'Select problem finder identifiers',
        options: currentProblemFinderIds.map(id => ({label: id, value: id})),
        isLoading: loadingCurrentProblemFinderIds,
        loadingMessage: 'Loading problem finders...',
        allowMultiSelect: true,
    });

    // Using a type that extends ProblemFilter with a unique key
    type ProblemFilterWithKey = ProblemFilter & { key: string };
    const [problemFilters, setProblemsFilters] = React.useState<ProblemFilterWithKey[]>([]);

    const [webhookConfiguration, resetWebhookInput, setWebhookInput, webhookErrorOrIncomplete, WebhookTemplate] = useWebhookTemplate();

    const [workOrderConfiguration, resetWorkOrderInput, setWorkOrderInput, workOrderErrorOrIncomplete, WorkOrderTemplate] = useWorkOrderTemplate();

    const defaultSimConfig = {
        accessLevel: undefined,
        assignedGroup: undefined,
        assignee: undefined,
        assignmentMap: undefined,
        autoUpgrade: undefined,
        categorization: undefined,
        closureStrategy: undefined,
        concerning: undefined,
        dedupeStrategy: undefined,
        descriptionTemplate: undefined,
        escalationMap: undefined,
        estimatedCompletionTime: undefined,
        estimatedStartTime: undefined,
        expirationStrategy: undefined,
        notificationGrouping: undefined,
        problemResolver: false,
        relationshipToConcerning: undefined,
        requester: undefined,
        suppressionConfiguration: undefined,
        titleTemplate: undefined,
        tags: [
            {
                tagId: 'ID4'
            },
            {
                tagId: reportConfigurationIdToUse
            }
        ]
    };

    const [simConfiguration, setSimConfiguration] = React.useState<SimConfiguration>(defaultSimConfig);

    const [cronInput, setCronInput] = React.useState<string>(undefined);
    const cronError = useMemo(() => getCronError(cronInput), [cronInput]);
    const [enabled, setEnabled] = React.useState<boolean>(false);


    React.useEffect(() => {
        if (reportConfigurationIdToUse) {
            // Update the tags to reflect the new `reportConfigurationIdToUse`
            setSimConfiguration((prevConfig) => {
                const updatedTags = prevConfig.tags.filter(tag => tag.tagId !== previousReportConfigIdRef.current);
                previousReportConfigIdRef.current = reportConfigurationIdToUse;
                const newTags = [
                    { tagId: reportConfigurationIdToUse },
                ];
                return {
                    ...prevConfig, // Spread previous configuration to preserve other properties
                    tags: [...updatedTags, ...newTags], // Add the new tags to the existing ones
                };
            });
        }
    }, [reportConfigurationIdToUse]);

    /**
     * Effect which will get the current report configurations.
     */
    React.useEffect(() => {
        // Initially clear the problem finder registrations when the region changes
        setCurrentProblemFinderIds([]);
        setLoadingCurrentProblemFinderIds(true);

        const fetchProblemFinderRegistrations = async () => {
            const registrations = await listProblemFinderRegistrations(webStageConfig, selectedRegion);
            setCurrentProblemFinderIds(registrations.map(reg => reg.problemFinderId));
        };

        fetchProblemFinderRegistrations()
            .catch(err => {
                setAlertMessage({
                    type: 'error',
                    message: err.message,
                    title: 'Error loading known problem finder identifiers.'
                });
                console.error(err);
            })
            .finally(() => setLoadingCurrentProblemFinderIds(false));
    }, [selectedRegion]);

    /**
     * Effect which will retrieve the problem finder registration from the URL param ID.
     */
    React.useEffect(() => {
        if (inEditMode) {
            const fetchReportConfiguration = async () => {
                const existingRegistration = await getReportConfiguration(webStageConfig, selectedRegion, {
                    reportConfigurationId: reportConfigurationId
                });
                setProblemFinderIds(existingRegistration.problemFinderIds);
                setPermissionsTeam(existingRegistration.permissionsTeam);
                setProcessingMode(existingRegistration.processingMode);
                setProblemsFilters(existingRegistration.problemFilters.map(filter => ({
                    ...filter,
                    key: uuidv4()
                })));
                setWebhookInput(existingRegistration.notifications.webhookConfiguration);
                setWorkOrderInput(existingRegistration.notifications.workOrderConfiguration);
                setSimConfiguration(existingRegistration.notifications.simConfiguration ?? defaultSimConfig);
                setEnabled(existingRegistration.enabled);
                setReportConfigExecutionState(existingRegistration.reportConfigExecutionState);
                setCronInput(existingRegistration.scheduleCronExpression);
            };

            fetchReportConfiguration()
                .catch(err => {
                    setAlertMessage({
                        type: 'error',
                        message: err.message,
                        title: 'Error loading report configuration.'
                    });
                    console.error(err);
                });
        }
    }, [inEditMode, selectedRegion]);

    /**
     * Are all the required inputs filled out to create a report configuration?
     *
     * @returns {boolean} true if all the required inputs filled out to create a report configuration
     */
    function isFormComplete(): boolean {
        return !!reportConfigurationIdToUse
            && !!permissionsTeam
            && problemFinderIds.length > 0
            && (problemFilters.length === 0 || problemFilters.every(pf => pf.value !== undefined && pf.type !== undefined && pf.clusivity !== undefined))
            && cronInput !== undefined && cron(cronInput).isValid()
            && (!webhookErrorOrIncomplete || !workOrderErrorOrIncomplete || validateSimConfiguration(simConfiguration).valid);
    }

    /**
     * Set the problem filter result for the given key.
     * Using useCallback to prevent unnecessary re-renders of child components.
     *
     * @param problemFilter problem filter
     * @param filterKey unique filter key
     */
    const setProblemFilterResult = React.useCallback((problemFilter: ProblemFilter, filterKey: string) => {
        setProblemsFilters(prevState => {
            return prevState.map(filter => 
                filter.key === filterKey 
                    ? { ...problemFilter, key: filterKey }
                    : filter
            );
        });
    }, []);

    /**
     * Remove the problem filter with the given key.
     * Using useCallback to prevent unnecessary re-renders of child components.
     *
     * @param filterKey unique filter key
     */
    const removeProblemFilter = React.useCallback((filterKey: string) => {
        setProblemsFilters(prevState => {
            return prevState.filter(filter => filter.key !== filterKey);
        });
    }, []);

    /**
     * Clears the page inputs.
     */
    function clearInputs(): void {
        setReportConfigurationIdToUse(undefined);
        setPermissionsTeam(undefined);
        setProblemFinderIds([]);
        setProblemsFilters([]);
        setEnabled(false);
        setCronInput(undefined);
        resetWebhookInput();
        resetWorkOrderInput();
        setSimConfiguration(undefined);
    }

    /**
     * Callback for submitting a report configuration.
     */
    function onSubmit(): void {
        setSubmitted(true);
        putReportConfiguration(webStageConfig, selectedRegion, {
            reportConfigurationId: reportConfigurationIdToUse,
            reportConfigurationDetails: {
                enabled: enabled,
                scheduleCronExpression: cronInput,
                problemFinderIds: problemFinderIds as string[],
                problemFilters: problemFilters.map(({key, ...filter}) => filter),
                notifications: {
                    webhookConfiguration: !webhookErrorOrIncomplete ? webhookConfiguration : undefined,
                    workOrderConfiguration: !workOrderErrorOrIncomplete ? workOrderConfiguration : undefined,
                    simConfiguration: validateSimConfiguration(simConfiguration).valid ? simConfiguration : undefined
                },
                permissionsTeam: permissionsTeam,
                processingMode: processingMode
            }
        })
            .then(response => {
                // TODO: add response to list of reports
                setAlertMessage({
                    type: 'success',
                    message: `Successfully ${inEditMode ? 'updated' : 'created'} report configuration: `,
                    link: {
                        text: response.reportConfiguration.reportConfigurationId,
                        href: `${RouteName.REPORT_CONFIGURATIONS}/${response.reportConfiguration.reportConfigurationId}`
                    }
                });
                // Only clear inputs after creating a new configuration, not when updating
                if (!inEditMode) {
                    clearInputs();
                }
            })
            .catch(err => {
                setAlertMessage({
                    type: 'error',
                    message: err.message,
                    title: 'Error creating report configuration'
                });
            })
            .finally(() => {
                setSubmitted(false);
            });
    }

    return (
        <Column alignmentHorizontal='start' spacingInset='500'>
            <BreadcrumbGroup>
                <Breadcrumb href={RouteName.REPORT_CONFIGURATIONS}>Report Configurations</Breadcrumb>
                <Breadcrumb>{inEditMode ? reportConfigurationId : 'Creating'}</Breadcrumb>
            </BreadcrumbGroup>
            {
                alertMessage && (
                    <Alert
                        type={alertMessage.type}
                        title={alertMessage.title}
                        onClose={() => {
                            setAlertMessage(undefined);
                            setSubmitted(false);
                        }}
                    >
                        {alertMessage.message}
                        {
                            alertMessage.link && (
                                <Link onClick={() => navigate(alertMessage.link.href)}>{alertMessage.link.text}</Link>
                            )
                        }
                    </Alert>
                )
            }
            {
                inEditMode && (
                    <Link href={RouteName.REPORT_CONFIGURATIONS + '/create'}>
                        Not looking to update an existing configuration? Click here to create one instead.
                    </Link>
                )
            }
            <Column>
                <Box type='outline' spacingInset='400'>
                    <Input
                        id='report-config-id-input'
                        value={reportConfigurationIdToUse}
                        onChange={(val) => setReportConfigurationIdToUse(val)}
                        type='text'
                        error={reportConfigurationIdToUse === '' || !new RegExp('[A-Z]([A-Z0-9]*[a-z][a-z0-9]*[A-Z]|[a-z0-9]*[A-Z][A-Z0-9]*[a-z])[A-Za-z0-9]*').test(reportConfigurationIdToUse)}
                        label={`Report Configuration ID *`}
                        helperText={'Unique Identifier for Report Configuration'}
                        disabled={inEditMode}
                        width='100%'
                    />
                </Box>
                <Box type='outline' spacingInset='400'>
                    <Column width='100%' alignmentHorizontal='start'>
                        <Text type='h300'>Problem Filtering</Text>
                        {ProblemFinderIdsSelect}
                        <Column width='100%' alignmentHorizontal='start'>
                            {
                                problemFilters.map((pf) => {
                                    return (
                                        <ProblemFilterInput
                                            key={pf.key}
                                            setProblemFilterResult={setProblemFilterResult}
                                            existingFilter={pf}
                                            idx={pf.key}
                                            onRemoveClicked={removeProblemFilter}
                                        />
                                    );
                                })
                            }
                            <Button type='secondary' onClick={() => {
                                setProblemsFilters(prevState => [...prevState, {
                                    value: undefined,
                                    type: undefined,
                                    clusivity: undefined,
                                    key: uuidv4()
                                }]);
                            }}>
                                <Icon tokens={plusCircleTokens}>Add filter</Icon>Add filter
                            </Button>
                        </Column>
                    </Column>
                </Box>
                <Box type='outline' spacingInset='400'>
                    <Column spacing='450'>
                        <Text type='h300'>Permissions</Text>
                        <Input
                            id='permissions-team-input'
                            value={permissionsTeam}
                            onChange={setPermissionsTeam}
                            type='text'
                            helperText='A team from https://permissions.amazon.com/ which will control who can edit this report configuration'
                            label='Team'
                            width='100%'
                            error={permissionsTeam === ''}
                            errorMessage={permissionsTeam === '' && 'Permissions team cannot be empty'}
                        />
                    </Column>
                </Box>
                <Box type='outline' spacingInset='400'>
                    <Column spacing='450'>
                        <Text type='h300'>Processing Configuration</Text>
                        <Select
                            label='Processing mode'
                            value={processingMode}
                            onChange={setProcessingMode}
                            placeholder='Select processing mode'
                            helperText='Defines how problems are processed for this report configuration'
                            width='100%'
                        >
                            <SelectOption label='Batch Scheduled' value='BATCH_SCHEDULED' />
                            <SelectOption label='Event Driven' value='EVENT_DRIVEN' />
                            <SelectOption label='Shadow Event Driven' value='SHADOW_EVENT_DRIVEN' />
                        </Select>
                    </Column>
                </Box>
                <Row alignmentVertical='stretch' widths='fill' spacing='500' wrap='down'>
                    <Box type='outline' spacingInset='400' minWidth={400}>
                        <Column spacing='450'>
                            <Text type='h300'>Message Configuration</Text>
                            <Text type='b100' color='secondary'>Note that you are not required to fill out all of the message
                                configurations.</Text>
                            <ControlledExpander title='Webhook Configuration'>
                                {WebhookTemplate}
                            </ControlledExpander>
                            <ControlledExpander title='SIM Configuration'>
                                <SimTemplate
                                    simConfiguration={simConfiguration}
                                    setTitleTemplate={(titleTemplate: string) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        titleTemplate
                                    }))}
                                    setDescriptionTemplate={(descriptionTemplate: string) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        descriptionTemplate
                                    }))}
                                    setAssignee={(assignee: SimIdentity) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        assignee
                                    }))}
                                    setAssignedGroup={(assignedGroup: SimIdentity) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        assignedGroup
                                    }))}
                                    setRequester={(requester: SimIdentity) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        requester
                                    }))}
                                    setCategorization={(categorization: SimCategorizationEntry[]) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        categorization
                                    }))}
                                    setEscalationMap={(escalationMap: EscalationMap) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        escalationMap
                                    }))}
                                    setAssignmentMap={(assignmentMap: AssignmentMap) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        assignmentMap
                                    }))}
                                    setEstimatedStartTime={(estimatedStartTime: number) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        estimatedStartTime
                                    }))}
                                    setEstimatedCompletionTime={(estimatedCompletionTime: number) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        estimatedCompletionTime
                                    }))}
                                    setTags={(tags: SimTag[]) => setSimConfiguration(prevState => ({...prevState, tags}))}
                                    setAccessLevel={(accessLevel: SimAccessLevel) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        accessLevel
                                    }))}
                                    setAutoUpgrade={(autoUpgrade: SimAutoUpgradePolicy) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        autoUpgrade
                                    }))}
                                    setConcerning={(concerning: string) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        concerning
                                    }))}
                                    setRelationshipToConcerning={(relationshipToConcerning: string) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        relationshipToConcerning
                                    }))}
                                    setNotificationGrouping={(notificationGrouping: NotificationGrouping) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        notificationGrouping
                                    }))}
                                    setDedupeStrategy={(dedupeStrategy: DedupeStrategy) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        dedupeStrategy
                                    }))}
                                    setExpirationStrategy={(expirationStrategy: StatusChangeStrategy) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        expirationStrategy
                                    }))}
                                    setClosureStrategy={(closureStrategy: StatusChangeStrategy) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        closureStrategy
                                    }))}
                                    setProblemResolver={(problemResolver: boolean) => setSimConfiguration(prevState => ({
                                        ...prevState,
                                        problemResolver
                                    }))}
                                />
                            </ControlledExpander>
                            <ControlledExpander title='Work Order Configuration'>
                                {WorkOrderTemplate}
                            </ControlledExpander>
                        </Column>
                    </Box>
                    <Box type='outline' spacingInset='400' minWidth={400}>
                        <Column alignmentHorizontal='start'>
                            <SchedulingInput
                                enabled={enabled}
                                cronError={cronError}
                                setEnabled={setEnabled}
                                cronExpression={cronInput}
                                setCronExpression={setCronInput}
                                enabledText='Enable report configuration?'
                                secondaryText='Select a fixed period to run the report configuration. Alternatively, define your own Cron expression.'
                            />
                        </Column>
                    </Box>
                </Row>
            </Column>
            <LoadingButton
                text={!inEditMode ? 'Create' : 'Update'}
                isLoading={submitted}
                disabled={!isFormComplete() || submitted}
                onClick={onSubmit}
            />
        </Column>
    );
};

export default CreateReportConfiguration;
